import { Data, Effect } from 'effect';
import { okraApi } from '../okraApi';
import type {
  EnvironmentItem,
  EnvironmentConnection,
  EnvironmentAgent,
  EnvironmentSession,
  EnvironmentEvent,
  EnvironmentAccessRule,
} from '../types';

export class EnvironmentsEndpointError extends Data.TaggedError('EnvironmentsEndpointError')<{
  readonly operation: 'resource-url' | 'claim-url';
}> {}

const environmentsEndpointCauses = new WeakMap<EnvironmentsEndpointError, unknown>();

function environmentsEndpointFailure(
  operation: EnvironmentsEndpointError['operation'],
  cause: unknown,
): EnvironmentsEndpointError {
  const error = new EnvironmentsEndpointError({ operation });
  environmentsEndpointCauses.set(error, cause);
  return error;
}

export function environmentsEndpointErrorCause(error: EnvironmentsEndpointError): unknown {
  return environmentsEndpointCauses.get(error);
}

export const environmentResourceUrlEffect = Effect.fn('web.api.environments.resourceUrl')(
  function* (id: string, suffix = '') {
    return yield* Effect.try({
      try: () => `/v1/environments/${id}${suffix}`,
      catch: (cause) => environmentsEndpointFailure('resource-url', cause),
    });
  },
);

export const claimInfoUrlEffect = Effect.fn('web.api.environments.claimInfoUrl')(function* (
  token: string,
) {
  return yield* Effect.try({
    try: () => `/v1/inboxes/claim-info/${encodeURIComponent(token)}`,
    catch: (cause) => environmentsEndpointFailure('claim-url', cause),
  });
});

function environmentResourceUrl(id: string, suffix = ''): string {
  return Effect.runSync(environmentResourceUrlEffect(id, suffix).pipe(Effect.orDie));
}

function claimInfoUrl(token: string): string {
  return Effect.runSync(claimInfoUrlEffect(token).pipe(Effect.orDie));
}

export const environmentsEndpoints = okraApi.injectEndpoints({
  endpoints: (build) => ({
    listEnvironments: build.query<{ data: EnvironmentItem[] }, void>({
      query: () => '/v1/environments',
      providesTags: ['Environments'],
    }),
    createEnvironment: build.mutation<EnvironmentItem, { name: string }>({
      query: (body) => ({ url: '/v1/environments', method: 'POST', body }),
      invalidatesTags: ['Environments'],
    }),
    getEnvironment: build.query<EnvironmentItem, string>({
      query: (id) => environmentResourceUrl(id),
      providesTags: (_r, _e, id) => [{ type: 'Environments', id }],
    }),
    getEnvironmentConnections: build.query<{ data: EnvironmentConnection[] }, string>({
      query: (id) => environmentResourceUrl(id, '/connections'),
      providesTags: (_r, _e, id) => [{ type: 'Environments', id }],
    }),
    getEnvironmentAgents: build.query<{ data: EnvironmentAgent[] }, string>({
      query: (id) => environmentResourceUrl(id, '/agents'),
      providesTags: (_r, _e, id) => [{ type: 'Environments', id }],
    }),
    getEnvironmentSessions: build.query<{ data: EnvironmentSession[] }, string>({
      query: (id) => environmentResourceUrl(id, '/sessions'),
      providesTags: (_r, _e, id) => [{ type: 'Environments', id }],
    }),
    getEnvironmentEvents: build.query<{ data: EnvironmentEvent[] }, string>({
      query: (id) => environmentResourceUrl(id, '/events?limit=200'),
      providesTags: (_r, _e, id) => [{ type: 'Environments', id }],
    }),
    getEnvironmentAccessRules: build.query<{ data: EnvironmentAccessRule[] }, string>({
      query: (id) => environmentResourceUrl(id, '/access-rules'),
      providesTags: (_r, _e, id) => [{ type: 'Environments', id }],
    }),
    putEnvironmentAccessRules: build.mutation<
      { data: EnvironmentAccessRule[] },
      {
        environmentId: string;
        rules: Array<{
          subject_type: 'email' | 'domain' | 'user_id';
          subject: string;
          role: 'viewer' | 'editor' | 'admin';
          status?: 'active' | 'disabled';
        }>;
      }
    >({
      query: ({ environmentId, rules }) => ({
        url: environmentResourceUrl(environmentId, '/access-rules'),
        method: 'PUT',
        body: { rules },
      }),
      invalidatesTags: (_r, _e, { environmentId }) => [{ type: 'Environments', id: environmentId }],
    }),
    postEnvironmentEvent: build.mutation<
      { answer?: string | null; status: string },
      { environmentId: string; text: string; channel?: string; externalThreadId?: string }
    >({
      query: ({ environmentId, text, channel, externalThreadId }) => ({
        url: environmentResourceUrl(environmentId, '/events'),
        method: 'POST',
        body: {
          channel: channel ?? 'web',
          external_thread_id: externalThreadId ?? 'dashboard-chat',
          events: [{ type: 'user.message', content: [{ type: 'text', text }] }],
        },
      }),
      invalidatesTags: (_r, _e, { environmentId }) => [{ type: 'Environments', id: environmentId }],
    }),
    getClaimInfo: build.query<
      {
        email: string | null;
        table_id: string;
        table_name: string | null;
        rows_count: number;
        expires_at: string;
        already_claimed: boolean;
        expired: boolean;
      },
      string
    >({
      query: (token) => claimInfoUrl(token),
    }),
    claimInbox: build.mutation<
      { table_id: string; agent_id: string; email: string | null },
      { claim_token: string }
    >({
      query: (body) => ({
        url: '/v1/inboxes/claim',
        method: 'POST',
        body,
      }),
    }),
    exportTableToSheets: build.mutation<
      { spreadsheetId?: string; url?: string; error?: string },
      { tableId: string }
    >({
      query: ({ tableId }) => ({
        url: `/table/${tableId}/export/sheets`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useListEnvironmentsQuery,
  useCreateEnvironmentMutation,
  useGetEnvironmentQuery,
  useGetEnvironmentConnectionsQuery,
  useGetEnvironmentAgentsQuery,
  useGetEnvironmentSessionsQuery,
  useGetEnvironmentEventsQuery,
  useGetEnvironmentAccessRulesQuery,
  usePutEnvironmentAccessRulesMutation,
  usePostEnvironmentEventMutation,
  useGetClaimInfoQuery,
  useClaimInboxMutation,
  useExportTableToSheetsMutation,
} = environmentsEndpoints;
